import { Injectable } from '@angular/core';
import { FunctionLifeCycle } from '../telephonyutils';
import { VoiceProvider } from '../telephony.provider';
import { AuthenticationService, Logger } from '@app/core';
import { Observable, Subscription, catchError, finalize, first, firstValueFrom, lastValueFrom, map, of, tap, throwError } from 'rxjs';
import { TelephonyHelperService } from '../telephony.helper.service';
import { IbexTelephonyService } from './ibex.telephony.service';
import { ToastService } from '../../../services/toast/toast.service';
import { Constants, EVENT_NAMES, EVENT_TYPES } from '@app/constants';
import { FormattedEmail } from '@app/core/helpers';
import { AppConfigurationService } from '../../../services/app.configuration.service';
import { Status } from '../../../features/chat/components/transcripts/transcripts.component';
import { environment } from '@env/environment';
import { debug } from 'console';
import { CallType } from '../../../features/call/interfaces/CallType/CallType';

const log = new Logger('IbexVoiceProvider');

@Injectable({ providedIn: 'root' })
export class IbexVoiceProvider extends VoiceProvider {
  private currentConference: string = '';
  private callerId = '';
  private currentCallSid: string = '';
  private thirdPartyCallSid: string = '';
  private primaryCampaign: any = {};
  private customerCallSid: string = '';
  private reservation: any;
  private isCallAnswered: boolean = false;
  private isCallDialed: boolean = false;
  private directDispose: boolean = false;
  private isConferenceLoaded: boolean = false;
  private leadId: string = '';
  private secondary_caller_ids?: Array<any> | null;
  private isVoiceConnected: boolean = false;
  private sipConfig: any = {};
  private extension: string = '';
  private voiceStatus = false;
  private agentIdentity = '';
  private call: any;
  private isMuted: boolean = false;
  status = ''
  private insightsVoice = false;
  private lastStatus: any = null;
  private isFirstVoiceConnected = false;
  private number: string = '';
  private eventName: string = '';
  private primary_call_sid: string = '';
  private dialingForm: string = '';
  private insightsAsteriskSettings: any = {};
  private cancelStatusRequest?: Subscription;
  constructor(private telephonyService: IbexTelephonyService, private telephonyHelperService: TelephonyHelperService, private toastService: ToastService, public auth: AuthenticationService, private appConfig: AppConfigurationService) {
    super();
    if (this.auth.isAuthenticated()) {
      this.agentIdentity = FormattedEmail(this.auth.authData.Username, false);
    }
    this.auth.onlogout.subscribe((data: any) => {
      const provider = this.auth.getTelephonyProvider();
      if (provider == 'ibex') {
        this.telephonyService.ibexLogout();
      }
    });
    this.events.subscribe((event) => {
      switch (event.type) {
        case 'Status':
          switch (event.name) {
            case 'status-change':
              const data = event.data;
              this.agentIdentity = FormattedEmail(this.auth.authData.Username, false);
              if (data.agentIdentity == this.agentIdentity) {
                this.onStatusChange(data);
              }
              break;
          }
          break;
        case 'Insights':
          switch (event.name) {
            case 'voice-init':
              this.initializeInsightVoice(event.data);
              break;
          }
          break;
        case EVENT_TYPES.OutboundDialer:
          switch (event.name) {
            case EVENT_NAMES.LeadSet:
              this.leadId = event.data;
              break;
          }
          break;
      }
    });
  }

  async init(lf: FunctionLifeCycle | null, campaign: any) {
    lf?.onStart();
    this.setCampaign(campaign);
    const callSid = localStorage.getItem('activeCallSid');
    if (callSid) {
      this.forceDisposedAndLogout(null, callSid, false);
    }
    else {
      this.markPreviousCallComplete();
    }
    this.login();
    lf?.onEnd();
  }
  async loadInsightSettings(lf: FunctionLifeCycle | null) {
    await lastValueFrom(this.telephonyService.loggedInInsightUser());
  }
  async login() {
    try {
      const hasMicrophone = await this.telephonyHelperService.checkMicrophone();
      if (!hasMicrophone) {
        log.info('Mic not enabled');
        this.toastService.info('Please enable microphone to get calls');
        return;
      }
      this.setStatusLoader(true);
      this.loadInsightSettings(null);
      this.toastService.info("Registering agent voice phone ....", true);
      try {
        const value: any = await lastValueFrom(this.telephonyService.loginStatus());
        if (value) {
          await this.telephonyService.ibexLogout();
        }
      } catch (error) {
        console.error('Logout failed:', error);
      }
      const campaigns = this.auth.authData.UserSettings.IbexCampaign || [];
      const settings: any = await lastValueFrom(this.telephonyService.ibexLogin(campaigns));
      this.isVoiceConnected = settings.IsVoiceConnected;
      this.sipConfig = settings.AI[0];
      this.extension = settings.D;
      localStorage.setItem('ibex_login', "true");
      this.registerAsteriskEvents();
    } catch (error) {
      this.setStatusLoader(false);
      console.log(new Date().toLocaleString() + '  ➤  Error in login', error);
      this.toastService.error('Agent Not registered. Facing technical issue at switch level.');
    }

  }
  public setCampaign(campaign: any) {
    this.callerId = campaign.CallerId;
    this.primaryCampaign = campaign;
    this.secondary_caller_ids = campaign.SecondaryCallerIds;
  }
  registerAsteriskEvents() {
    window.AsteriskConnect(this.sipConfig.B, this.sipConfig.B, 8089, this.sipConfig.C, this.sipConfig.E, (conn: any) => {
      switch (conn.type) {
        case 'started':
          const extension = this.extension;
          window.AsteriskRegister((e: any) => {
            if (e.type == 'connected' && e.session == window.registerSession) {
              window.establishVoiceConnection(extension, (obj: any) => {
                this.registerInboundEvents(obj)
              });
              //this.telephonyHelperService.playAudio();
            }
            else if (e.type == 'terminated') {
              this.toastService.error('Agent voice phone connectivity terminated.');
              this.setVoiceStatus(false);
              this.setStatusLoader(false);
              this.isFirstVoiceConnected = true;
            }
          });
          break;
        case 'stopped':
        case 'terminated':
          this.toastService.error(`Agent voice phone connectivity ${conn.type}`);
          this.setVoiceStatus(false);
          console.log(`ASTERISK_CONNECT ${conn.type}. Agent voice phone connectivity terminated.`, conn);
          this.setStatusLoader(false);
          this.isFirstVoiceConnected = true;
          break;
        case 'failed_to_start':
          this.toastService.error(`Failed to start voice connection. Please make sure you are connected and using IBEX network.`);
          this.setVoiceStatus(false);
          this.setStatusLoader(false);
          console.log(`ASTERISK_CONNECT ${conn.type}.Failed to start voice connection. Please make sure you are connected and using IBEX network.`, conn);
          this.isFirstVoiceConnected = true;
          break;
        case 'i_new_call':
          console.info('start stack event = ');
          break;
      }
    });
  }
  registerInboundEvents(e: any) {
    let voice_status = false;
    switch (e.type) {
      case 'connecting':
        voice_status = false;
        this.toastService.info('Agent voice phone connecting ...', true);
        this.setStatusLoader(true);
        break;
      case 'connected':
        this.toastService.info('Agent voice phone registered.', true);
        voice_status = true;
        this.setStatusLoader(false);
        this.setVoiceStatus(voice_status);
        if (!this.call) {
          const status = !this.isFirstVoiceConnected ? Constants.STATUSES.LoggedIn : Constants.STATUSES.Break;
          this.setIbexStatus(status);
          this.postStatusChange(status);
        }

        this.isFirstVoiceConnected = true;
        break;
      case 'terminated':
        voice_status = false;
        this.toastService.error(`Agent voice phone connectivity ${e.type}`, true);
        console.log(`INBOUND_CALLBACK_EVENTS ${e.type}. Agent voice phone connectivity ${e.type}`);
        this.setStatusLoader(false);
        this.isFirstVoiceConnected = true;
        this.setVoiceStatus(false);
        break;
      case 'm_permission_refused':
        voice_status = false;
        this.toastService.error(`Agent voice phone connectivity ${e.type}`, true);
        console.log(`INBOUND_CALLBACK_EVENTS ${e.type}. Agent voice phone connectivity ${e.type}`);
        this.setStatusLoader(false);
        this.isFirstVoiceConnected = true;
        this.setVoiceStatus(false);
        break;
      case 'i_ao_request':
      case 'm_early_media':
      case 'm_stream_audio_remote_added':
        voice_status = false;
        break;
    };
  }
  onStatusChange(data: any) {
    switch (data.status) {
      case "OnDisconnect":
        this.setVoiceStatus(false);
        break;
      case "OnCall":
        this.handleOnCall(data);
        break;
      case "OnWrapup":
        if (this.call) {
          this.events.next({ type: 'Reservation', name: 'false', data: '' });
          const callbacks: FunctionLifeCycle = { onStart: () => { }, onEnd: () => { } };
          this.setIbexStatus(Constants.STATUSES.Wrap);
          this.endCall(callbacks, false);
        }
        break;
      case "OnConferenceStateChange":
        this.handleConferenceStateChange(data);
        break;
      case "OnError":
        this.handleOnError();
        break;
    }
  }
  setIbexStatus(status: any, isIbexStatus: boolean = false) {
    if (isIbexStatus) {
      this.setStatusLoader(true);
      let params = { CampaignId: this.primaryCampaign.Code, Code: status.FriendlyName == 'Wait' ? "" : status.ibex_status };
      this.cancelStatusRequest = this.telephonyService.updateIbexStatus(params)
        .subscribe({
          next: (response: any) => {
            this.postStatusChange(status);
          },
          error: async (err: any) => {
            const message = err.error.title;
            console.log(message);
            if (message.includes('Agent is already on break')) {
              this.postStatusChange(status);
            }
            else if (message.includes('Agent is not on break')) {
              if (status.FriendlyName == 'Wait') {
                this.postStatusChange(Constants.STATUSES.Wait);
              }
              else {
                this.postStatusChange(status);
              }
            }
            else if (message.includes("Operation not valid for this state")) {
              this.sync(null, null, null, this.call ? true : false);
            }

          }
        });
    }
    else {
      this.postStatusChange(status);
    }
  }
  endCall(lifeCycle: FunctionLifeCycle | null, isDirect: boolean) {
    if (this.call) {
      if (isDirect) {
        this.directDispose = true;
      }
    }
    this.events.next({ type: 'Call', name: 'disconnect', data: 'args' });
  }
  async disposeCall(lifecyle: FunctionLifeCycle, status: string) {
    const callSid = this.customerCallSid;
    const lead = JSON.parse(this.call.G);

    window.UnMuteCall();
    this.events.next({ type: 'Call', name: 'mute', data: '' });

    this.isMuted = false;

    const endIbexCall = (callSid: string, termCode: string, cancelThirdParty: boolean, lead: any) => {
      const endCall = () => {
        const regex = /^S\d*$/;
        const isSale = regex.test(termCode);
        if (isSale) {
          termCode = "S1"
        }
        this.telephonyService.endCall({ CallSid: callSid, TermCode: termCode, Lead: lead })
          .subscribe((res: any) => {
            this.toastService.success('Call ended');
          });
      }
      if (cancelThirdParty) {
        this.telephonyService.endThirdParty({ CustomerCallSid: callSid }).subscribe({
          next: (response: any) => {
            endCall();
          },
          error: (err: any) => {
            console.log(new Date().toLocaleString() + '  ➤  Third party call not ended successfully');
            endCall();
          }
        });
      }
      else {
        endCall();
      }
      this.resetState();
      if (this.voiceStatus) {
        this.setIbexStatus(Constants.STATUSES[status]);
      }
      else {
        this.toastService.info("You have been put to Break status as your voice is not connected. Please reconnect voice to get calls.");
        this.setIbexStatus(Constants.STATUSES.Break); //setting earlier for reserve status
      }
    }

    this.events.next({
      type: 'Call',
      name: 'ibex-dispose',
      data: ''
    });
    this.events.next({
      type: 'Call',
      name: 'dispose',
      data: {
        callSid: callSid,
        lead: lead,
        callback: endIbexCall
      }
    });
  }

  async changeStatus(lifecyle: FunctionLifeCycle, status: string) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    const hasMicrophone = await this.telephonyHelperService.checkMicrophone();
    if (!hasMicrophone) {
      log.info('Mic not enabled');
      this.toastService.info('Please enable microphone to get calls');
      return;
    }
    try {
      this.events.next({ type: 'Activity', name: 'init', data: '' });
      this.status = Constants.STATUSES[status];
      this.setIbexStatus(this.status, true);
    } catch (error) {
      log.error(`${this.auth.authData.Username} => fetchWorkerActivities error`);
      this.toastService.error(`Unable to change your status, Please try again`);
      this.events.next({ type: 'Activity', name: 'failed', data: '' });
    }
  }
  async handleOnCall(conn: any) {
    try {
      if (this.cancelStatusRequest) {
        this.cancelStatusRequest.unsubscribe();
        this.cancelStatusRequest = undefined;
      }
    } catch (error) {
      console.log(error);
    }
    console.log(new Date().toLocaleString() + '  ➤  handleOnCall Events ==> ', conn.state);
    const state = JSON.parse(conn.state);
    const campaigns = JSON.parse(state.Campaigns);
    const callCampaign = campaigns.Item1;
    const tfnCampaign = campaigns.Item2;
    this.events.next({ type: 'Reservation', name: 'created', data: '' });
    this.call = state.Call;
    this.customerCallSid = this.call.C;
    const lead = JSON.parse(this.call.G);
    const countryCode = `+${lead.country}`;
    const callType = this.call.B == 3 ? 'outbound' : 'inbound';
    const to = callType == 'inbound' ? this.normalizeNumber(lead.DNIS, countryCode) : this.normalizeNumber(lead.ccallidnum, countryCode);
    let from = this.normalizeNumber(lead.tel, countryCode);
    this.isCallDialed = true;
    this.events.next({ type: 'Call', name: 'init', data: '' });
    this.isCallAnswered = true;
    let data: any = {};
    let previewLead = false;
    let testLead = from.toLowerCase().includes('agt01');
    let isSierraCall = this.getScriptSkillCode('SIERRA_SKILL_TFNS')?.includes(to);
    let headers = this.call.X ? JSON.parse(this.call.X) : {};
    if (isSierraCall && headers['X-BTN']) {
      console.log(new Date().toLocaleString() + " | Sierra skill call with headers ", headers);
      from = this.normalizeNumber(headers['X-BTN'], countryCode);
    }
    if (callType == 'outbound') {
      let isPortmoreCall = this.getScriptSkillCode('PORTMORE_SKILLCODE').includes(tfnCampaign.Code);
      data = {
        campaignId: callCampaign.Id,
        campaignCode: callCampaign.Code,
        campaignName: callCampaign.Name,
        tfnCampaignId: isPortmoreCall ? tfnCampaign.Id : callCampaign.Id,
        tfnCampaignCode: isPortmoreCall ? tfnCampaign.Code : callCampaign.Code,
        tfnCampaignName: isPortmoreCall ? tfnCampaign.Name : callCampaign.Name,
        btn: testLead ? environment.testNumber : from, //'+12022899898', 
        tfn: to,
        callSid: this.customerCallSid,
        callType: callType,
        isCallBack: false,
        langCode: this.primaryCampaign.LangCode ? this.primaryCampaign.LangCode : 'en',
        leadId: this.leadId,
        primary_call_sid: this.primary_call_sid,
        dialing_type: this.dialingForm,
        headers: headers
      };
    }
    else {
      data = {
        campaignId: callCampaign.Id,
        campaignCode: callCampaign.Code,
        campaignName: callCampaign.Name,
        tfnCampaignId: tfnCampaign.Id,
        tfnCampaignCode: tfnCampaign.Code,
        tfnCampaignName: tfnCampaign.Name,
        btn: testLead ? environment.testNumber : from, //'+12022899898', 
        tfn: to,
        callSid: this.customerCallSid,
        callType: callType,
        isCallBack: false,
        langCode: 'en',
        leadId: "",
        primary_call_sid: this.primary_call_sid,
        dialing_type: this.dialingForm,
        headers: headers
      };
      previewLead = true;
      this.leadId = "";
    }
    localStorage.setItem('activeCallSid', this.customerCallSid);
    localStorage.setItem('ibexLead', JSON.stringify(lead));
    this.events.next({ type: 'Call', name: 'accept', data: data });
    this.toastService.success('Call accepted');
    this.setIbexStatus(Constants.STATUSES.Busy);
    setTimeout(() => {
      if (previewLead) {
        const isLeadPreview = this.primaryCampaign.OutboundDialerEnabled;
        if (isLeadPreview) {
          this.leadId = '';
          this.events.next({
            type: EVENT_TYPES.OutboundDialer,
            name: EVENT_NAMES.Init,
            data: {
              campaignId: this.primaryCampaign.Id,
              lead_id: '',
              email: '',
              phone_number: testLead ? environment.testNumber : from, // '+12022899898'
              direction: CallType.Inbound,
              type: 'reservation',
            },
          });
        }
      }
    }, 50);
  }
  async makeCall(
    lifeCycle: FunctionLifeCycle,
    to: string,
    callType: string = 'outbound',
    attributes: any = {},
    callBackTFN: any = '',
    callCampaign: any = {},
    countryCode: any = '',
    primary_call_sid: any = '',
    dialingForm: string = ''
  ) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    this.isCallDialed = true;
    this.events.next({ type: 'Call', name: 'init', data: '' });
    this.isCallAnswered = true;
    let code = this.getCountryCode(to);
    let data: any = {
      CampaignId: this.primaryCampaign.Code,
      LeadId: this.leadId,
      NumberField: to.replace(code, ''),
      EndCodeField: "",
      CountryCode: code.replace('+', ''),
      OverrideTimezone: true,
      Iscallback: false,
      CallerId: ''
    }
    if (callType == 'outbound') {
      data.CampaignId = this.primaryCampaign.Code;
    }
    else if (callType == 'callback') {
      let isPortmoreCall = this.getScriptSkillCode('PORTMORE_SKILLCODE').includes(callCampaign?.tfnCampaignCode);
      code = isPortmoreCall ? callCampaign.tfnCampaignCode : callCampaign.Code;
      data.CampaignId = code;
    }
    else {
      //portmore pass tfn campaign code as campaign code
      let code = '';
      let isPortmoreCall = this.getScriptSkillCode('PORTMORE_SKILLCODE').includes(callCampaign.tfnCampaignCode);
      code = isPortmoreCall ? callCampaign.tfnCampaignCode : callCampaign.Code;
      data.CampaignId = code;
    }
    const campaigns = this.auth.authData.UserSettings.IbexCampaign || [];
    if (campaigns.find((c: any) => c == data.CampaignId) == undefined) {
      console.log(new Date().toLocaleString() + " | Skill not assigned to agent, passing call skill");
      data.CampaignId = callCampaign.Code;
    }
    this.primary_call_sid = primary_call_sid;
    this.dialingForm = dialingForm;
    this.telephonyService.manualCall(data).subscribe({
      next: (response: any) => {
      },
      error: (error) => {
        console.log(new Date().toLocaleString() + '  ➤  manual call error response', error);
        this.toastService.error('Unable to make call. Please try again later');
        this.events.next({ type: 'Call', name: 'close-modal', data: '' });
      }
    });
  }
  muteUnMuteCall(lifecyle: FunctionLifeCycle) {
    if (!this.call) {
      throw new Error('No call available');
    }
    if (this.call) {
      if (this.isMuted) {
        window.UnMuteCall();
      } else {
        window.MuteCall();
      }
      this.events.next({ type: 'Call', name: 'mute', data: '' });
      this.isMuted = !this.isMuted;
    }
  }
  consultCall(lifecyle: FunctionLifeCycle, number: string, OnConference: boolean) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    if (!this.call) {
      this.toastService.warning('No active call found. Please try again');
      return;
    }
    this.eventName = "consultancy";
    this.events.next({ type: 'Consult', name: 'init', data: { callSid: this.customerCallSid } });
    let code = this.getCountryCode(number);
    this.number = number;
    const data = {
      CountryCode: '1',
      Number: number.replace(code, ''),
      Lead: JSON.parse(this.call.G),
      HoldCutomer: true,
      CallerId: '',
      CustomerCallSid: this.customerCallSid,
      IsSipConference: false,
      AgentID: this.auth.authData.UserSettings?.IbexId
    };
    const vivint_numbers = this.getVivintConfig('VIVINT_TFNS');
    if (vivint_numbers.includes(data.Number)) {
      data.IsSipConference = true;
    }
    this.telephonyService
      .consult(data)
      .subscribe({
        next: (response: any) => {
          this.events.next({ type: 'Consult', name: 'completed', data: '' });
          this.toastService.success('Consultancy established successfully');
          this.eventName = '';
        },
        error: (err: any) => {
          this.toastService.error('Consultancy failed. Please try again.');
          this.events.next({ type: 'Consult', name: 'failed', data: '' });
        },
      });
  }
  conferenceCall(lifecyle: FunctionLifeCycle, number: string, OnConsult: boolean) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    if (!this.call) {
      this.toastService.warning('No active call found. Please try again');
      return;
    }
    this.eventName = "conference";
    this.events.next({ type: 'Conference', name: 'init', data: { callSid: this.customerCallSid } });
    let code = this.getCountryCode(number);
    this.number = number;
    const data = {
      CountryCode: '1',
      Number: number.replace(code, ''),
      Lead: JSON.parse(this.call.G),
      HoldCutomer: false,
      CallerId: '',
      OnConsult: OnConsult,
      CustomerCallSid: this.customerCallSid,
      IsSipConference: false,
      AgentID: this.auth.authData.UserSettings?.IbexId
    };
    const vivint_numbers = this.getVivintConfig('VIVINT_TFNS');
    if (vivint_numbers.includes(data.Number)) {
      data.IsSipConference = true;
    }
    this.telephonyService.conference(data).subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Conference', name: 'completed', data: '' });
        this.toastService.success('Conference call established successfully');
        this.eventName = '';
      },
      error: (err: any) => {
        this.toastService.error('Conference failed. Please try again.');
        this.events.next({ type: 'Conference', name: 'failed', data: '' });
      }
    });
  }
  async transferCall(lifecyle: FunctionLifeCycle, number: string, onConsultancy: boolean, onConference: boolean) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    if (!this.call) {
      this.toastService.warning('No active call found. Please try again');
      return;
    }
    let code = this.getCountryCode(number);
    this.number = number;
    let data = {
      CountryCode: "1",
      Number: number.replace(code, ''),
      TransferCase: "",
      TargetCampaign: this.primaryCampaign.Code,
      JsonLead: JSON.parse(this.call.G),
      CallerId: '',
      CallSid: this.customerCallSid
    }
    if (onConsultancy || onConference) {
      data.TransferCase = 'completeConference';
    } else {
      data.TransferCase = 'telcoBlindTransfer';
      const vivint_numbers = this.getVivintConfig('VIVINT_TFNS');
      if (vivint_numbers.includes(data.Number)) {
        this.toastService.info('Direct vivint transfer not allowed');
        return;
      }
    }

    this.events.next({ type: 'Transfer', name: 'init', data: '' });

    this.telephonyService.transferCall(data).subscribe({
      next: (response: any) => {
        //debugger;
        let validate = false;
        if (this.getScriptSkillCode('PKCHAT_TFNS')?.includes(data.Number)) {
          validate = true;
        }
        this.events.next({ type: 'Transfer', name: 'completed', data: validate });
        this.toastService.success('Call transfered successfully');
      },
      error: (err: any) => {
        this.toastService.success('Some error occurred while transferring call');
        this.events.next({ type: 'Transfer', name: 'failed', data: '' });
      }
    });
  }
  endThirdPartyCall(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    if (!this.call) {
      this.toastService.warning('No active call found. Please try again');
      return;
    }
    this.eventName = "cancel";
    this.events.next({ type: 'Cancel', name: 'init', data: '' });

    this.telephonyService.endThirdParty({ CustomerCallSid: this.customerCallSid }).subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Cancel', name: 'completed', data: '' });
        this.eventName = '';
      },
      error: (err) => {
        this.toastService.success('Some error occurred while ending third party call');
        this.events.next({ type: 'Cancel', name: 'failed', data: '' });
      }
    });
  }
  toggleCall(lifecyle: FunctionLifeCycle, isCustomerConnected: boolean, onConsultancy: boolean, OnConference: boolean, number: string) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    if (!this.call) {
      this.toastService.warning('No active call found. Please try again');
      return;
    }

    this.events.next({ type: 'Toggle', name: 'init', data: '' });
    isCustomerConnected = !isCustomerConnected;

    this.telephonyService.toggle({ OnConference, CustomerCallSid: this.customerCallSid, IsConnectToCustomer: isCustomerConnected, Number: number }).subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Toggle', name: 'completed', data: isCustomerConnected });
        this.toastService.success('Call toggled successfully');
      },
      error: (err) => {
        console.log(err);
        this.toastService.success('Some error occurred while toggling call');
        this.events.next({ type: 'Toggle', name: 'failed', data: '' });
      }
    });
  }
  async holdCall(lifecyle: FunctionLifeCycle, onHold: boolean) {
    if (!this.voiceStatus) {
      this.toastService.warning('Agent not registered. Please try again');
      return;
    }
    this.eventName = onHold ? 'resume' : 'hold';
    this.events.next({ type: 'Hold', name: 'init', data: '' });

    this.telephonyService.hold(!onHold, this.customerCallSid)
      .subscribe({
        next: (response: any) => {
          this.events.next({ type: 'Hold', name: 'completed', data: '' });
          this.postStatusChange(onHold ?
            Constants.STATUSES.Busy :
            Constants.STATUSES.Hold);
          this.toastService.success(`Call ${onHold ? 'resumed' : 'hold'} successfully`);
          this.eventName = '';
        },
        error: (err) => {
          this.toastService.error('Failed to update call status. Please try again.');
          this.events.next({ type: 'Hold', name: 'failed', data: '' });
        },
      });
  }
  destroy(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) {

  }
  acceptCall(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) { }
  rejectCall(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) { }
  sendDTMF(lifecyle: FunctionLifeCycle, number: string) {
    const result = window.sipSendDTMF(number);
    if (result == 0) {
      try { this.telephonyHelperService.playAudio('dtmf.wav') } catch (e) { }
    }
  }
  outboundCallHangup(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) {

  }
  async getQueuedCalls(
    lifecyle: FunctionLifeCycle,
    campaignCode: string,
    direction: number,
    selectedDialingCountry: string | null
  ) {
    let response: any;
    if (direction == 0) {
      response = await lastValueFrom(
        this.telephonyService.getQueuedCalls(this.auth.authData.UserSettings?.IbexProgram)
      );
      response.leads.sort((a: any, b: any) => b.duration - a.duration);
    }
    else {
      response = await lastValueFrom(
        this.telephonyService.getOutboundQueuedCalls("", campaignCode, direction, selectedDialingCountry)
      );
    }
    return response;
  }
  async monitorCall(lifecyle: FunctionLifeCycle, customerCallSid: string) {
    if (!this.insightsVoice) {
      this.toastService.error('Voice not initialized, please wait');
      return;
    }
    const calls: any = await lastValueFrom(this.telephonyService.getActiveCalls());

    const call = calls.find((x: any) => x.C1 == customerCallSid);
    if (!call) {
      this.toastService.error('Live call not found for monitoring');
      this.events.next({ type: 'Monitor', name: 'remove', data: customerCallSid });
      return;
    }

    this.events.next({ type: 'Monitor', name: 'init', data: customerCallSid });
    setTimeout(() => {
      window.sendDtmfString(call.C9 + '#');
      this.events.next({ type: 'Monitor', name: 'accept', data: customerCallSid });
    }, 500);
  }
  stopMonitorCall(lifecyle: FunctionLifeCycle) {
    window.sendDtmfString(['0']);
    this.events.next({ type: 'Monitor', name: 'disconnect', data: '' });
  }
  callMonitorOperation(lifecyle: FunctionLifeCycle, customerCallSid: string, monitorType: string) {
    let isWisper = false,
      isBarge = false;
    switch (monitorType) {
      case 'wisper':
        isWisper = true;
        isBarge = false;
        break;
      case 'barge':
        isWisper = false;
        isBarge = true;
        break;
    }
    this.events.next({ type: 'MonitorOperation', name: 'init', data: '' });
    let dtmf = '4';
    if (isWisper) {
      dtmf = '5';
    }
    if (isBarge) {
      dtmf = '6';
    }
    setTimeout(() => {
      window.sendDtmfString(dtmf);
      this.events.next({ type: 'MonitorOperation', name: 'completed', data: monitorType });
    }, 500);
  }
  getCurrentStatus() {
    const curr_status: any = JSON.parse(localStorage.getItem('last_status') || '{}');
    const current = curr_status.status;
    return current;
  }
  async sync(lifecyle: FunctionLifeCycle | null, _status: any, lastStatus: any, onCall: any) {
    const current = this.getCurrentStatus()
    const user: any = await lastValueFrom(this.telephonyService.getAgentCurrentStatus());
    if (onCall && user.Status == 'OnWrapup' && current == 'Busy') { //on call on front , wrap event received on server, and current busy on front
      console.log(new Date().toLocaleString() + " | onCall && user.Status == 'OnWrapup' && current == 'Busy'", user)
      this.setIbexStatus(Constants.STATUSES.Wrap);
      this.endCall(null, false);
    }
    else if (!onCall && user.Status == 'OnWrapup' && current != 'Busy' && current != 'Wrap') {
      this.events.next({ type: 'Reservation', name: 'false', data: '' });
      console.log(new Date().toLocaleString() + " | !onCall && user.Status == 'OnWrapup' && current != 'Busy' && current != 'Wrap'", user)
      this.toastService.error('Your status has been changed to Break due to network glitch');
      let obj = JSON.parse(user.Lead);
      const lead = JSON.parse(obj.Call.G);
      const callsid = lead.callid;
      lead.termcd = 'FD';
      this.telephonyService.forceDisposeAndHangupCall(callsid, this.auth.authData.Id, lead).then(() => {
        this.setStatusLoader(false);
        this.setIbexStatus(Constants.STATUSES.Break, true);
      });
    }
    else if (user.Status == 'OnCall' && current != 'Busy') {
      console.log(new Date().toLocaleString() + " | user.Status == 'OnCall' && current != 'Busy'", user)
      if (this.voiceStatus) {
        let obj = JSON.parse(user.Lead);
        const params = { state: user.Lead, campaign: obj.Call.A };
        this.handleOnCall(params);
      }
      else {
        this.toastService.error('Your status has been changed to Break due to network glitch');
        let obj = JSON.parse(user.Lead);
        const lead = JSON.parse(obj.Call.G);
        const callsid = lead.callid;
        lead.termcd = 'FD';
        this.telephonyService.forceDisposeAndHangupCall(callsid, this.auth.authData.Id, lead).then(() => {
          this.setStatusLoader(false);
          this.setIbexStatus(Constants.STATUSES.Break, true);
        });
      }
    }
    else if (user.Status == 'OnBreak') {
      console.log(new Date().toLocaleString() + " | user.Status == 'OnBreak'")
      const statuses = ['Break', 'LoggedIn', 'Wrap', 'Hold', 'Callback', 'Training', 'CoachingCounseling', 'TeamMeeting'];
      if (statuses.indexOf(current) == -1) {
        console.log(new Date().toLocaleString() + " | statuses.indexOf(current) = true", user)
        setTimeout(() => {
          this.setStatusLoader(false);
          if (this.getCurrentStatus() != 'Break') {
            this.toastService.error('Your status has been changed to Break due to network glitch');
            this.setIbexStatus(Constants.STATUSES.Break, true);
          }
        }, 1000);
      }
      if (user.Lead) {
        console.log(new Date().toLocaleString() + " | user.Lead = true")
        let obj = JSON.parse(user.Lead);
        const lead = JSON.parse(obj.Call.G);
        const callsid = lead.callid;
        lead.termcd = 'FD';
        this.telephonyService.forceDisposeAndHangupCall(callsid, this.auth.authData.Id, lead).then(() => { });
      }
    }
    else if (user.Status == '' && user.Lead) {
      console.log(new Date().toLocaleString() + " | user.Lead = true")
      let obj = JSON.parse(user.Lead);
      const lead = JSON.parse(obj.Call.G);
      const callsid = lead.callid;
      lead.termcd = 'FD';
      this.telephonyService.forceDisposeAndHangupCall(callsid, this.auth.authData.Id, lead).then(() => { });
    }

    console.log(new Date().toLocaleString() + '  ➤  SYNC STATUS => ', user);
  }
  setOutboundCallSid(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) {

  }
  closeExistingConnections(lifecyle?: FunctionLifeCycle | null | undefined, ...args: any[]) {

  }
  setStatusLoader(value: boolean) {
    this.events.next({ type: 'Activity', name: 'set-loading', data: value });
  }
  async postStatusChange(status: any) {
    //for double events for multiple campaigns from ibex.
    if (this.lastStatus?.FriendlyName == status.FriendlyName) {
      this.setStatusLoader(false);
      return;
    }
    this.lastStatus = status;
    let callSid = this.customerCallSid;
    this.events.next({ type: 'Activity', name: 'init', data: '' });
    let response: any;
    let data = {
      Status: status.FriendlyName,
      UserId: this.auth.authData.Id,
      Version: '1.0.0',
      Campaigns: this.auth.authData.Campaigns?.map((x: any) => ({ Id: x.Id, Name: x.Name, Code: x.Code })),
      CallSid: callSid,
    };
    response = await lastValueFrom(this.telephonyService.addUserStatusDB(data));
    this.events.next({
      type: 'Activity',
      name: 'update',
      data: { activity: status.FriendlyName, date: response.Id },
    });
  }
  resetState() {
    this.call = null;
    this.customerCallSid = '';
    this.number = '';
    this.eventName = '';
    this.primary_call_sid = '';
    this.dialingForm = '';
    localStorage.removeItem('activeCallSid');
    localStorage.removeItem('ibexLead');
  }
  getCountryCode(phoneNumber: string) {
    let code = '';
    let countryCodes = this.appConfig.getByIdentifier('CountryDialingCodes')?.Fields;
    for (let i = 0; i < countryCodes?.length; i++) {
      if (phoneNumber.startsWith(countryCodes[i].dialing_code)) {
        code = countryCodes[i].dialing_code;
        return code;
      }
    }
    code = '+1'; // set +1 if no match is found
    return code;
  }
  async initializeInsightVoice(center: string) {
    if (this.insightsVoice) {
      return;
    }
    if (center == 'All' || center == '') {
      this.toastService.error('Insights voice can not be initialized for All center, please select specific center');
      return;
    }
    this.setStatusLoader(true);
    this.toastService.info('Initializing insights voice ...', true);
    const settings: any = await lastValueFrom(this.telephonyService.loggedInInsightUser());
    this.insightsAsteriskSettings = settings.Settings.find((x: any) => x.Location == center);
    if (!this.insightsAsteriskSettings) {
      this.toastService.error('No insights voice settings found for this center');
      return;
    }
    this.setupInsightsVoice();
  }
  setupInsightsVoice() {
    window.AsteriskConnect(this.insightsAsteriskSettings.AsteriskIP, this.insightsAsteriskSettings.AsteriskIP, parseInt(this.insightsAsteriskSettings.AsteriskPort), this.insightsAsteriskSettings.AsteriskUser, this.insightsAsteriskSettings.AsteriskPassword, (e: any) => {
      if (e.type == 'started') {
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
        window.AsteriskRegister((e: any) => {
          if (e.type == 'connected') {
            window.establishVoiceConnection(this.insightsAsteriskSettings.AsteriskExtension, (e: any) => {
              this.registerInsightVoiceEvents(e);
            });
          } else if (e.type == 'terminated') {
            this.insightsVoice = false;
            this.setStatusLoader(false);
            this.toastService.error('Insights voice is not registered please refresh...');
            this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
          }
        });
      }
      else if (e.type == 'stopped') {
        this.stopTone('ringbacktone');
        this.stopTone('ringtone');
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
      }
      else if (e.type == 'm_permission_refused') {
        this.stopTone('ringbacktone');
        this.stopTone('ringtone');
      }
      else if (e.type == 'failed_to_start') {
        this.insightsVoice = false;
        this.toastService.error('Insights voice not registered, please refresh...');
        this.setStatusLoader(false);
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
      }
    });
  }
  registerInsightVoiceEvents(e: any) {
    switch (e.type) {
      case 'connecting':
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
        break;
      case 'connected':
        this.setStatusLoader(false);
        this.insightsVoice = true;
        window.sendDtmfString(this.insightsAsteriskSettings.AsteriskDTMFid);
        setTimeout(() => {
          window.sendDtmfString(this.insightsAsteriskSettings.AsteriskDTMFPassword);
          this.toastService.info('Voice is registered successfully...', true);
        }, 1000);
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: true });
        break;
      case 'terminated':
      case 'terminating':
        this.setStatusLoader(false);
        this.stopTone('ringbacktone');
        this.stopTone('ringtone');
        this.insightsVoice = false;
        this.toastService.error('Voice is not registered please refresh...');
        this.events.next({ type: 'Voice', name: 'insightsVoiceStatus', data: false });
        break;
      case 'i_ao_request':
        if (e.session == window.oSipSessionCall) {
          const iSipResponseCode = e.getSipResponseCode();
          if (iSipResponseCode == 180 || iSipResponseCode == 183) {
            //this.startRingbackTone();
          }
        }
        break;
      case 'm_early_media':
        if (e.session == window.oSipSessionCall) {
          this.stopTone('ringbacktone');
          this.stopTone('ringtone');
        }
        break;
    }
  }
  stopTone(name: string) {
    try {
      const audio = document.getElementById('ringtone') as HTMLAudioElement;
      if (audio) {
        audio.pause();
      }
    }
    catch (e) { console.log(e); }
  }
  startRingbackTone() {
    try {
      const audio = document.getElementById('ringbacktone') as HTMLAudioElement;
      if (audio) {
        audio.play();
      }
    }
    catch (e) { console.log(e); }
  }
  setVoiceStatus(status: boolean) {
    this.voiceStatus = status;
    this.events.next({
      type: 'Voice',
      name: 'status',
      data: status,
    });
  }
  updateCallStatus(status: string, consultNo = '', conferenceNo = '') {
    this.telephonyService.updateCallStatus({ CallSid: this.customerCallSid, Status: status, ConsultNumber: consultNo, ConferenceNumber: conferenceNo }).subscribe((res: any) => {

    });
  }
  handleConferenceStateChange(data: any) {
    this.eventName = '';
    const state = JSON.parse(data.state);
    console.log(`handleConferenceStateChange => Conference state change data: ${state.State}, ThirdPartyBridge: ${state.ThirdPartyBridge}`);

    switch (state.State) {
      case 0:
        if (state.ThirdPartyBridge) {
          this.events.next({ type: 'Cancel', name: 'third-party-bridge', data: '' });
          this.toastService.success('Customer hangup the call');
        }
        else {
          this.events.next({ type: 'Cancel', name: 'completed', data: '' });
          this.toastService.success('Third party call ended successfully');
        }
        setTimeout(() => {
          this.telephonyService.updateCallStatus({ CallSid: this.customerCallSid, Status: 'Connected' }).subscribe((res: any) => { });
        }, 1000);
        break;
      case 1:
        this.events.next({ type: 'Conference', name: 'completed', data: '' });
        break;
      case 2:
        this.events.next({ type: 'Consult', name: 'completed', data: '' });
        break;
    }
  }
  handleOnError() {
    switch (this.eventName) {
      case "consultancy":
        this.toastService.warning('You have dialed an incorrect number');
        break;
      case "conference":
        this.toastService.warning('You have dialed an incorrect number');
        break;
      case "hold":
        this.toastService.error('You can not hold your call in conference');
        break;
      case "resume":
        this.toastService.error('You can not resume your call');
        break;
      case "cancel":
        this.toastService.error('You can not cancel consultancy or Conference call');
        break;
      default:
        this.setStatusLoader(false);
        break;
    }
  }
  reconnectVoice(lifecyle: FunctionLifeCycle) {
    try {
      if (window.registerSession) {
        window.AsteriskDisconnect();
      }
      window.location.reload();
    } catch (error) {
      console.log(error);
    }
    // finally {
    //   this.setVoiceStatus(false);
    //   this.setStatusLoader(true);
    //   this.toastService.info('Reconnecting voice...');
    //   this.registerAsteriskEvents();
    // }
  }
  async forceDisposedAndLogout(lifecyle: FunctionLifeCycle | null, call_sid: string | null, logout: boolean = false) {
    let lead: any = localStorage.getItem('ibexLead');
    if (call_sid && lead) {
      lead = JSON.parse(lead);
      lead.termcd = 'FD';
      await this.telephonyService.forceDisposeAndHangupCall(call_sid, this.auth.authData.Id, lead);
      if (logout) {
        this.telephonyService.ibexLogout();
      }
      localStorage.removeItem('ibexLead');
    }
  }
  reserveStatus(lifecyle: FunctionLifeCycle | null, status: any) {
    let params = { CampaignId: this.primaryCampaign.Code, Code: status.FriendlyName == 'Wait' ? "" : status.ibex_status };
    this.telephonyService.updateIbexStatus(params).subscribe((res) => {

    });
  }
  markPreviousCallComplete() {
    this.telephonyService.markCallComplete().subscribe(_ => { });
  }
  getVivintConfig(attr: string): string {
    return this.appConfig.getByIdentifier('ScriptSkillCodesAndTFNS')?.Fields?.[attr] ?? "";
  }
  normalizeNumber(number: string, countryCode: string) {
    // If already starts with +, return as-is
    if (number.includes(countryCode))
      return number;

    return countryCode + number;
  }
  getScriptSkillCode(attr: string): string {
    return this.appConfig.getByIdentifier('ScriptSkillCodesAndTFNS')?.Fields?.[attr] ?? Constants[attr];
  }
}


// {
//   "callSid": "TS42025032808014100076",
//   "clientId": "6013e6429d6a5c482cdab57b",
//   "campaign": "TS4",
//   "status": "OnCall",
//   "ibexAgentId": "T32",
//   "agentIdentity": "gkflecxio",
//   "state": "{\"Call\":{\"A\":\"TS4\",\"B\":2,\"C\":\"TS42025032808014100076\",\"D\":0,\"E\":0,\"F\":false,\"G\":\"{\\\"NumberField\\\":\\\"tel\\\",\\\"EndCodeField\\\":\\\"termcd\\\",\\\"AreaCodeField\\\":\\\"areacode\\\",\\\"LastCallType\\\":0,\\\"TimeZoneID\\\":0,\\\"TimeZoneName\\\":\\\"\\\",\\\"TimeZone\\\":\\\"\\\",\\\"Offset\\\":0,\\\"recordsortid\\\":0,\\\"country\\\":\\\"1\\\",\\\"areacode\\\":\\\"\\\",\\\"tel\\\":\\\"AGT01\\\",\\\"uniqueid\\\":\\\"TS42025032808014100020\\\",\\\"callid\\\":\\\"TS42025032808014100076\\\",\\\"lname\\\":\\\"\\\",\\\"mid\\\":\\\"\\\",\\\"fname\\\":\\\"\\\",\\\"addr\\\":\\\"\\\",\\\"city\\\":\\\"\\\",\\\"state\\\":\\\"\\\",\\\"zip\\\":\\\"\\\",\\\"lcdate\\\":\\\"2025-03-28T00:00:00-04:00\\\",\\\"lctime\\\":28901,\\\"termcd\\\":\\\"00\\\",\\\"agentid\\\":\\\"\\\",\\\"cbdate\\\":\\\"1900-01-01T00:00:00\\\",\\\"cbtime\\\":0,\\\"campaignid\\\":\\\"TS4\\\",\\\"acdgroup\\\":\\\"\\\",\\\"RoutingQueueID\\\":\\\"\\\",\\\"callcount\\\":0,\\\"s__amount\\\":0.0,\\\"sales\\\":0,\\\"saleid\\\":\\\"\\\",\\\"dialerid\\\":0,\\\"dcallcount\\\":0,\\\"ccallidtxt\\\":\\\"\\\",\\\"ccallidnum\\\":\\\"\\\",\\\"imp_date\\\":\\\"1900-01-01T00:00:00\\\",\\\"DNIS\\\":\\\"4120000001\\\",\\\"cbReason\\\":\\\"\\\",\\\"dnc_flag\\\":false,\\\"dnc_date\\\":\\\"1900-01-01T00:00:00\\\",\\\"Scrubbed\\\":false,\\\"current_attempts_count\\\":0,\\\"CURRENT_LIVE_ATTEMPT\\\":0,\\\"DayLiveCallCount\\\":0,\\\"CurrentTermCodeCount\\\":0,\\\"ClientID\\\":\\\"\\\",\\\"UpdatedOn\\\":\\\"1900-01-01T00:00:00\\\",\\\"LeadSourceID\\\":0,\\\"CallbackRepeatCount\\\":0,\\\"Ucid\\\":\\\"\\\"}\",\"H\":true,\"I\":true},\"IsPrimaryAgent\":true,\"RecordingState\":0,\"IsHold\":false,\"IsCustomerOnHold\":false,\"ConferenceState\":0,\"WrapupReason\":0,\"Lead\":null,\"IsAutoAnswered\":true,\"TermCode\":null,\"ThirdPartyBridge\":false,\"Action\":\"OnCall\",\"AgentID\":\"T32\",\"Error\":null,\"Exception\":null,\"WebHookId\":\"b11d41a3c90149dbaebbfc982c2a3c91\",\"CampaignID\":\"TS4\"}"
// }